import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, 
  Circle, 
  Calendar, 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  Edit2, 
  Trash2, 
  Plus,
  AlertCircle,
  FileText,
  Link,
  Video,
  BookOpen,
  FileQuestion,
  Target, 
  BarChart2,
  Library // Добавляем новую иконку для ресурсов
} from 'lucide-react';
import { NewResourceDialog } from './NewResourceDialog';
import ReactDOM from 'react-dom';
import { ConfirmDialog } from './ConfirmDialog';
import { Task, RoadmapData, Resource } from '../types/roadmap';

interface ModernTaskListProps {
  jsonData: RoadmapData | null;
  onTaskToggle: (index: number) => void;
  completedTasks: number[];
  onMarkdownChange?: (newMarkdown: string) => void;
  onSaveToServer?: (roadmapData: RoadmapData) => Promise<void>;
  editable?: boolean;
  markdown?: string; // Оставляем для обратной совместимости, но не используем
}

interface ParsedTask {
  content: string;
  description?: string;
  level: number;
  index: number;
  subtasks: ParsedTask[];
  parentIndex?: number;
  deadline?: string;
  resources?: string[] | Resource[];
  completed?: boolean;
  indicator?: string; // Показатель выполнения задачи
  id: string;
}

interface EditTaskDialogProps {
  isOpen: boolean;
  task: {
    index: number;
    id: string;
    content: string;
    description?: string;
    deadline?: string;
    indicator?: string; // Показатель выполнения задачи
    resources?: string[] | Resource[];
  };
  onSave: (index: number, id: string, content: string, description: string, deadline: string, indicator: string, resources: any[]) => void;
  onCancel: () => void;
}

function EditTaskDialog({ isOpen, task, onSave, onCancel }: EditTaskDialogProps) {
  const [content, setContent] = useState(task.content);
  const [description, setDescription] = useState(task.description || '');
  const [deadline, setDeadline] = useState(task.deadline || '');
  const [indicator, setIndicator] = useState(task.indicator || '');
  const [resources, setResources] = useState<any[]>(task.resources || []);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setContent(task.content);
    setDescription(task.description || '');
    setDeadline(task.deadline || '');
    setIndicator(task.indicator || '');
    setResources(task.resources || []);
  }, [task]);

  if (!isOpen) return null;

  const removeResource = (index: number) => {
    setResources(resources.filter((_, i) => i !== index));
  };

  const getResourceTitle = (resource: any): string => {
    if (typeof resource === 'string') {
      // Пытаемся извлечь заголовок из строки в формате markdown [title](url)
      const match = resource.match(/\[(.*?)\]/);
      return match ? match[1] : resource;
    }
    return resource.name || resource.title || 'Ресурс';
  };

  const getResourceUrl = (resource: any): string => {
    if (typeof resource === 'string') {
      // Пытаемся извлечь URL из строки в формате markdown [title](url)
      const match = resource.match(/\[.*?\]\((.*?)\)/);
      return match ? match[1] : '#';
    }
    return resource.link || resource.url || '#';
  };

  const handleAddResource = (resource: { name: string; link: string; type?: string }) => {
    setResources([...resources, resource]);
    setShowResourceDialog(false);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        animation: 'fadeIn 0.2s ease-in-out'
      }}
      onClick={handleOverlayClick}
      onMouseDown={e => e.stopPropagation()}
    >
      <motion.div
        ref={dialogRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4"
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Редактирование задачи
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Название задачи
            </label>
            <input
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Описание (до 500 символов)
            </label>
            <textarea
              value={description}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setDescription(e.target.value);
                }
              }}
              className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
              rows={4}
              maxLength={500}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
              {description.length}/500
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Дедлайн
            </label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Показатель выполнения
            </label>
            <input
              type="text"
              value={indicator}
              onChange={(e) => setIndicator(e.target.value)}
              className="w-full p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ресурсы
            </label>
            <div className="space-y-2 mb-2">
              {resources.map((resource, index) => (
                <div key={index} className="flex items-center">
                  <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                    {getResourceTitle(resource)}
                  </span>
                  <button
                    onClick={() => removeResource(index)}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowResourceDialog(true);
              }}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center"
            >
              <Plus className="w-4 h-4 mr-1" />
              Добавить ресурс
            </button>
          </div>
        </div>
        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Отмена
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSave(task.index, task.id, content, description, deadline, indicator, resources);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Сохранить
          </button>
        </div>
      </motion.div>
      
      {/* Диалог добавления ресурса */}
      <NewResourceDialog 
        isOpen={showResourceDialog}
        onClose={() => setShowResourceDialog(false)}
        onAdd={handleAddResource}
      />
    </div>,
    document.body
  );
}

// Выносим ensureTaskId наружу, чтобы она была доступна везде
const ensureTaskId = (task: Task): string => {
  if (!task.id) {
    task.id = `temp_task_${Math.random().toString(36).substr(2, 9)}`;
  }
  return task.id;
};

export function ModernTaskList({ 
  jsonData, 
  onTaskToggle, 
  completedTasks, 
  onMarkdownChange, 
  onSaveToServer, 
  editable = true 
}: ModernTaskListProps) {
  // Убираем внутреннее состояние для модели и статусов. 
  // Теперь компонент полностью управляется через props.
  const model = jsonData;

  // Используем useMemo для вычисления статусов напрямую из jsonData.
  // Это гарантирует, что статусы всегда будут синхронизированы с props.
  const status = useMemo(() => {
    if (!model || !model.tasks) return {};
    const newStatus: Record<string, boolean> = {};
    const collectStatuses = (tasks: Task[]) => {
      tasks.forEach(task => {
        const taskId = ensureTaskId(task);
        newStatus[taskId] = task.completed || false;
        if (task.subtasks) {
          collectStatuses(task.subtasks);
        }
      });
    };
    collectStatuses(model.tasks);
    return newStatus;
  }, [model]);

  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [editTask, setEditTask] = useState<any | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<ParsedTask | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const initialLoadRef = useRef(true);

  // Используем useMemo для парсинга задач.
  const tasks = useMemo((): ParsedTask[] => {
    if (!model || !model.tasks) return [];
    
    const parsedTasks: ParsedTask[] = [];
    let taskIndex = 0;

    const processTask = (task: Task, level: number = 0, parentIndex?: number): void => {
      const taskId = ensureTaskId(task);
      const currentTaskIndex = taskIndex++;
      
      const parsed: ParsedTask = {
        content: task.title,
        description: task.description,
        level,
        index: currentTaskIndex,
        subtasks: [],
        parentIndex,
        deadline: task.deadline,
        resources: task.resources,
        completed: status[taskId] || false,
        indicator: task.indicator,
        id: taskId
      };

      if (task.subtasks) {
        task.subtasks.forEach(sub => {
          parsed.subtasks.push(processSubtask(sub, level + 1, currentTaskIndex));
        });
      }
      
      parsedTasks.push(parsed);
    };
    
    const processSubtask = (subtask: Task, level: number, parentIndex: number): ParsedTask => {
      const subtaskId = ensureTaskId(subtask);
      const currentTaskIndex = taskIndex++;
      
      return {
        content: subtask.title,
        description: subtask.description,
        level,
        index: currentTaskIndex,
        subtasks: [],
        parentIndex,
        deadline: subtask.deadline,
        resources: subtask.resources,
        completed: status[subtaskId] || false,
        indicator: subtask.indicator,
        id: subtaskId
      };
    };

    model.tasks.forEach(task => processTask(task));
    
    return parsedTasks;
  }, [model, status]);
  
  useEffect(() => {
    // Раскрываем задачи верхнего уровня с подзадачами по умолчанию при первой загрузке
    if (initialLoadRef.current && tasks.length > 0) {
        const topLevelTasks = tasks.filter(task => task.level === 0);
        const tasksToExpand = topLevelTasks
            .filter(task => (task.subtasks && task.subtasks.length > 0) || (task.resources && task.resources.length > 0))
            .map(task => task.id);
        setExpandedTasks(tasksToExpand);
        initialLoadRef.current = false;
    }
  }, [tasks]);

  const saveUpdatedModel = useCallback(async (newModel: RoadmapData) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (onSaveToServer) {
        await onSaveToServer(newModel);
      }
      if (onMarkdownChange) {
        onMarkdownChange(JSON.stringify(newModel, null, 2));
      }
    } catch (error) {
      console.error("Failed to save roadmap:", error);
    } finally {
      setIsSaving(false);
    }
  }, [onSaveToServer, onMarkdownChange, isSaving]);

  const handleTaskToggle = (taskId: string) => {
    if (!model) return;

    let newStatus: boolean | undefined;
    const newModel = JSON.parse(JSON.stringify(model)) as RoadmapData;

    const updateTaskStatus = (tasks: Task[], id: string): boolean => {
      for (const task of tasks) {
        if (task.id === id) {
          task.completed = !task.completed;
          newStatus = task.completed;
          
          // Update all subtasks
          if (task.subtasks) {
            task.subtasks.forEach(sub => updateAllSubtasks(sub, newStatus!));
          }
          return true;
        }
        if (task.subtasks && updateTaskStatus(task.subtasks, id)) {
          return true;
        }
      }
      return false;
    };

    const updateAllSubtasks = (task: Task, completed: boolean) => {
      task.completed = completed;
      if (task.subtasks) {
        task.subtasks.forEach(sub => updateAllSubtasks(sub, completed));
      }
    };
    
    updateTaskStatus(newModel.tasks, taskId);
    
    // Logic to update parent task status
    const updateParentStatus = (tasks: Task[]) => {
      tasks.forEach(task => {
        if (task.subtasks && task.subtasks.length > 0) {
          updateParentStatus(task.subtasks); // Recurse first
          const allSubtasksCompleted = task.subtasks.every(sub => sub.completed);
          if (task.completed !== allSubtasksCompleted) {
            task.completed = allSubtasksCompleted;
          }
        }
      });
    };

    updateParentStatus(newModel.tasks);
    
    saveUpdatedModel(newModel);
  };
  
  // Функция для переключения развернутого состояния задачи
  const toggleTaskExpand = (taskId: string) => {
    setExpandedTasks(prev => 
      prev.includes(taskId) 
        ? prev.filter(id => id !== taskId) 
        : [...prev, taskId]
    );
  };

  const addTask = (parentTaskId?: string) => {
    if (!model) return;
    const newModel = JSON.parse(JSON.stringify(model)) as RoadmapData;
    const newTaskId = `temp_task_${Math.random().toString(36).substr(2, 9)}`;
    const newTask: Task = {
      id: newTaskId,
      title: "Новая задача",
      description: "",
      completed: false,
      subtasks: []
    };

    if (parentTaskId) {
      const findAndAddTask = (tasks: Task[]): boolean => {
        for (const task of tasks) {
          if (task.id === parentTaskId) {
            if (!task.subtasks) task.subtasks = [];
            task.subtasks.push(newTask);
            return true;
          }
          if (task.subtasks && findAndAddTask(task.subtasks)) {
            return true;
          }
        }
        return false;
      };
      findAndAddTask(newModel.tasks);
    } else {
      newModel.tasks.push(newTask);
    }
    
    saveUpdatedModel(newModel);

    setTimeout(() => {
      const taskElement = document.getElementById(`task-${newTaskId}`);
      const editButton = taskElement?.querySelector('.edit-button');
      if (editButton instanceof HTMLElement) {
          editButton.click();
      }
    }, 100);
  };
  
  const confirmDelete = () => {
    if (!taskToDelete || !model) return;
    
    const newModel = JSON.parse(JSON.stringify(model)) as RoadmapData;

    const deleteTaskById = (tasks: Task[], id: string): Task[] => {
      return tasks.filter(task => {
        if (task.id === id) return false;
        if (task.subtasks) {
          task.subtasks = deleteTaskById(task.subtasks, id);
        }
        return true;
      });
    };

    newModel.tasks = deleteTaskById(newModel.tasks, taskToDelete.id);
    saveUpdatedModel(newModel);
    setShowConfirmDialog(false);
    setTaskToDelete(null);
  };

  // Функция проверки, выполнена ли задача
  const isTaskCompleted = (task: ParsedTask): boolean => {
    return status[task.id] === true;
  };

  // Функция для обработки редактирования задачи
  const handleEdit = (task: ParsedTask) => {
    console.log(`Начало редактирования задачи: ${task.content} (ID: ${task.id})`);
    setEditTask({
      index: task.index,
      id: task.id,
      content: task.content,
      description: task.description,
      deadline: task.deadline,
      indicator: task.indicator,
      resources: task.resources
    });
  };

  // Функция для обработки удаления задачи
  const handleDelete = (task: ParsedTask) => {
    setTaskToDelete(task);
    setShowConfirmDialog(true);
  };

  const handleSaveEdit = (index: number, id: string, content: string, description: string, deadline: string, indicator: string, resources: any[]) => {
    if (!model) return;
    const newModel = JSON.parse(JSON.stringify(model)) as RoadmapData;

    const updateTaskById = (tasks: Task[]): boolean => {
        for (const task of tasks) {
            if (task.id === id) {
                task.title = content;
                task.description = description;
                task.deadline = deadline;
                task.indicator = indicator;
                task.resources = resources;
                return true;
            }
            if (task.subtasks && updateTaskById(task.subtasks)) {
                return true;
            }
        }
        return false;
    };

    updateTaskById(newModel.tasks);
    saveUpdatedModel(newModel);
    setEditTask(null);
  };

  // Функции для работы с ресурсами
  const getResourceIcon = (resource: any) => {
    if (typeof resource === 'string') {
      // Для строковых ресурсов используем общую иконку
      return <FileText className="w-4 h-4 mr-2 text-blue-500" />;
    }
    
    // Для объектных ресурсов выбираем иконку по типу
    switch (resource.type) {
      case 'video':
        return <Video className="w-4 h-4 mr-2 text-red-500" />;
      case 'book':
        return <BookOpen className="w-4 h-4 mr-2 text-purple-500" />;
      case 'article':
        return <FileText className="w-4 h-4 mr-2 text-blue-500" />;
      default:
        return <Link className="w-4 h-4 mr-2 text-gray-500" />;
    }
  };

  const getResourceTitle = (resource: any): string => {
    if (typeof resource === 'string') {
      // Пытаемся извлечь заголовок из строки в формате markdown [title](url)
      const match = resource.match(/\[(.*?)\]/);
      return match ? match[1] : resource;
    }
    return resource.name || resource.title || 'Ресурс';
  };

  const getResourceUrl = (resource: any): string => {
    if (typeof resource === 'string') {
      // Пытаемся извлечь URL из строки в формате markdown [title](url)
      const match = resource.match(/\[.*?\]\((.*?)\)/);
      return match ? match[1] : '#';
    }
    return resource.link || resource.url || '#';
  };

  return (
    <div className="space-y-4 sm:space-y-6 dark:bg-gray-800 rounded-xl">
      {editable && (
        <div className="flex justify-end mb-2 sm:mb-4">
          <button
            onClick={(e) => {
              e.stopPropagation(); // Предотвращаем всплытие события
              addTask();
            }}
            className="p-1.5 sm:p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
            title="Добавить задачу"
          >
            <Plus className="w-5 sm:w-6 h-5 sm:h-6" />
          </button>
        </div>
      )}
      
      <div className="space-y-3 sm:space-y-4">
        {tasks.map((task) => (
          <div key={`task-${task.id}`} className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700">
            <div 
              className={`
                ${isTaskCompleted(task) 
                  ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500' 
                  : task.deadline && new Date(task.deadline) < new Date() 
                    ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                }
              `}
            >
              <div className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-3">
                  <div className="flex items-start gap-2 sm:gap-3 flex-1">
                    <button
                      disabled={isSaving}
                      onClick={(e) => {
                        e.stopPropagation(); // Предотвращаем всплытие события
                        handleTaskToggle(task.id);
                      }}
                      className={`mt-0.5 sm:mt-1 focus:outline-none transition-all ${
                        isSaving 
                        ? 'cursor-not-allowed opacity-50' 
                        : 'hover:scale-110'
                      }`}
                    >
                      {isTaskCompleted(task) ? (
                        <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                      ) : (
                        <Circle className="w-4 sm:w-5 h-4 sm:h-5 text-gray-400 dark:text-gray-500" />
                      )}
                    </button>
                    <div className="flex-1">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                        {task.content}
                      </h3>
                      
                      {task.description && (
                        <p className="mt-1 text-sm md:text-base text-gray-600 dark:text-gray-400">
                          {task.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                        {task.deadline && (
                          <div className={`flex items-center text-xs px-2 py-1 rounded-full ${
                            new Date(task.deadline) < new Date() && !isTaskCompleted(task)
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          }`}>
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(task.deadline).toLocaleDateString()}
                            {new Date(task.deadline) < new Date() && !isTaskCompleted(task) && (
                              <span className="ml-1">(Просрочено)</span>
                            )}
                          </div>
                        )}
                        
                        {task.subtasks && task.subtasks.length > 0 && (
                          <div className="flex items-center text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                            <span>{task.subtasks.length} подзадач{task.subtasks.length > 1 ? '' : 'а'}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                    
                  <div className="flex items-center mt-2 sm:mt-0 justify-end">
                    {editable && (
                      <div className="flex space-x-1 sm:space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Предотвращаем всплытие события
                            handleEdit(task);
                          }}
                          className="p-1 sm:p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full"
                        >
                          <Edit2 className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation(); // Предотвращаем всплытие события
                            handleDelete(task);
                          }}
                          className="p-1 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                        >
                          <Trash2 className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                        </button>
                      </div>
                    )}
                      
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Предотвращаем всплытие события
                        toggleTaskExpand(task.id);
                      }}
                      className="p-1 sm:p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full ml-1 sm:ml-2"
                    >
                      {expandedTasks.includes(task.id) ? (
                        <ChevronUp className="w-4 sm:w-5 h-4 sm:h-5" />
                      ) : (
                        <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <AnimatePresence>
                {expandedTasks.includes(task.id) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Ресурсы основной задачи */}
                    {task.resources && task.resources.length > 0 && (
                      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2 flex items-center">
                          <Library className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1.5 sm:mr-2 text-blue-500" />
                          Ресурсы
                        </h4>
                        <ul className="space-y-1.5 sm:space-y-2 pl-4 sm:pl-6">
                          {task.resources.map((resource, idx) => (
                            <li key={idx} className="text-sm md:text-base flex items-center">
                              {getResourceIcon(resource)}
                              <a 
                                href={getResourceUrl(resource)} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                {getResourceTitle(resource)}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Подзадачи */}
                    {task.subtasks && task.subtasks.length > 0 && (
                      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-sm md:text-base font-semibold text-gray-700 dark:text-gray-300 mb-1.5 sm:mb-2">
                          Подзадачи
                        </h4>
                        <div className="space-y-2 pl-3 sm:pl-6">
                          {task.subtasks.map((subtask) => (
                            <div 
                              key={`subtask-${subtask.id}`}
                              className={`p-2 sm:p-3 rounded-lg ${
                                isTaskCompleted(subtask)
                                  ? 'bg-green-50 dark:bg-green-900/20 border-l-2 border-green-500'
                                  : subtask.deadline && new Date(subtask.deadline) < new Date()
                                    ? 'bg-red-50 dark:bg-red-900/20 border-l-2 border-red-500'
                                    : 'bg-white dark:bg-gray-700 border-l-2 border-blue-500'
                              }`}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start">
                                <div className="flex items-start gap-2 flex-1">
                                  <button
                                    disabled={isSaving}
                                    onClick={(e) => {
                                      e.stopPropagation(); // Предотвращаем всплытие события
                                      handleTaskToggle(subtask.id);
                                    }}
                                    className={`mt-0.5 focus:outline-none transition-all ${
                                      isSaving 
                                      ? 'cursor-not-allowed opacity-50' 
                                      : 'hover:scale-110'
                                    }`}
                                  >
                                    {isTaskCompleted(subtask) ? (
                                      <CheckCircle2 className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-green-500" />
                                    ) : (
                                      <Circle className="w-3.5 sm:w-4 h-3.5 sm:h-4 text-gray-400 dark:text-gray-500" />
                                    )}
                                  </button>
                                  <div className="flex-1">
                                    <div className="flex justify-between">
                                      <h5 className="text-sm md:text-base font-medium text-gray-900 dark:text-white mb-0.5">
                                        {subtask.content}
                                      </h5>
                                      {editable && (
                                        <div className="flex space-x-1 ml-2">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation(); // Предотвращаем всплытие события
                                              handleEdit(subtask);
                                            }}
                                            className="p-0.5 sm:p-1 text-gray-500 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full"
                                          >
                                            <Edit2 className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation(); // Предотвращаем всплытие события
                                              handleDelete(subtask);
                                            }}
                                            className="p-0.5 sm:p-1 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {subtask.description && (
                                      <p className="mt-0.5 text-xs sm:text-sm md:text-sm text-gray-600 dark:text-gray-400">
                                        {subtask.description}
                                      </p>
                                    )}
                                    
                                    {subtask.indicator && (
                                      <div className="mt-1 sm:mt-2 flex items-start text-xs sm:text-sm">
                                        <BarChart2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 flex-shrink-0 mt-0.5 text-blue-500 dark:text-blue-400" />
                                        <span className="text-gray-600 dark:text-gray-400">
                                          <span className="font-semibold">Показатель:</span> {subtask.indicator}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {subtask.deadline && (
                                      <div className="mt-1 flex items-center text-xs sm:text-sm">
                                        <Clock className="w-3 h-3 mr-1 text-gray-500 dark:text-gray-400" />
                                        <span className={`${
                                          new Date(subtask.deadline) < new Date() && !isTaskCompleted(subtask)
                                            ? 'text-red-500'
                                            : 'text-gray-500 dark:text-gray-400'
                                        }`}>
                                          {new Date(subtask.deadline).toLocaleDateString()}
                                          {new Date(subtask.deadline) < new Date() && !isTaskCompleted(subtask) && (
                                            <span className="ml-1">(Просрочено)</span>
                                          )}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Ресурсы подзадачи */}
                                    {subtask.resources && subtask.resources.length > 0 && (
                                      <div className="mt-2">
                                        <div className="flex items-center text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                          <Library className="w-3 h-3 mr-1 text-blue-500" />
                                          <span>Ресурсы:</span>
                                        </div>
                                        <ul className="space-y-1 pl-4 mt-0.5">
                                          {subtask.resources.map((resource, idx) => (
                                            <li key={idx} className="text-xs sm:text-sm flex items-center">
                                              {getResourceIcon(resource)}
                                              <a 
                                                href={getResourceUrl(resource)} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-blue-600 dark:text-blue-400 hover:underline"
                                              >
                                                {getResourceTitle(resource)}
                                              </a>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Кнопка добавления подзадачи */}
                    {editable && (
                      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                        <div className="pl-3 sm:pl-6">
                          <button
                            onClick={(e) => {
                              e.stopPropagation(); // Предотвращаем всплытие события
                              addTask(task.id);
                            }}
                            className="flex items-center text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                          >
                            <Plus className="w-3.5 sm:w-4 h-3.5 sm:h-4 mr-1" />
                            Добавить подзадачу
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>
      
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmDialog(false)}
        title="Подтверждение удаления"
        message="Вы уверены, что хотите удалить эту задачу?"
        confirmText="Удалить"
        isDanger={true}
      />
      
      {editTask && (
        <EditTaskDialog
          isOpen={true}
          task={editTask}
          onSave={handleSaveEdit}
          onCancel={() => setEditTask(null)}
        />
      )}
    </div>
  );
}
